import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, NumberInput, Button, Flex, Badge, Select, SelectItem } from '@tremor/react';
import { api } from '../../lib/api';
import { PresetSelector } from '../PresetSelector';
import useSWR from 'swr';
import type { FeederPageResponse, FeederRecordResponse, FeederBatchRecordsResponse, FeederAllResponse, XeraPreset, DocumentCountResponse, BundlesResponse } from '../../types';

import { FileUploadTab } from './FileUploadTab';

type Mode = 'batch' | 'single' | 'justice' | 'upload';

// Known EFTA ID ranges per dataset (from justice.gov/epstein/doj-disclosures)
// EFTA IDs are globally sequential across datasets.
const JUSTICE_DATASET_STARTS: Array<{ dataset: number; firstEfta: number }> = [
  { dataset: 1,  firstEfta: 1 },
  { dataset: 2,  firstEfta: 3159 },
  { dataset: 3,  firstEfta: 3858 },
  { dataset: 4,  firstEfta: 5705 },
  { dataset: 5,  firstEfta: 8409 },
  { dataset: 6,  firstEfta: 8585 },
  { dataset: 7,  firstEfta: 9016 },
  { dataset: 8,  firstEfta: 9676 },
  { dataset: 9,  firstEfta: 39025 },
  { dataset: 10,  firstEfta: 1262782 },
  { dataset: 11,  firstEfta: 2205655 },
  { dataset: 12,  firstEfta: 2730265 },
];

function getKnownRange(dataset: number): { start: number; end: number } | null {
  const entry = JUSTICE_DATASET_STARTS.find(d => d.dataset === dataset);
  if (!entry) return null;
  const next = JUSTICE_DATASET_STARTS.find(d => d.dataset === dataset + 1);
  return { start: entry.firstEfta, end: next ? next.firstEfta - 1 : entry.firstEfta + 4999 };
}

interface ExternalBatchResult {
  success: boolean;
  batchId: string;
  batchName: string;
  summary: { total: number; queued: number; skipped: number; failed: number };
  message: string;
}

interface StartBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadOnly?: boolean;
}

export function StartBatchModal({ isOpen, onClose, uploadOnly = false }: StartBatchModalProps) {
  const [mode, setMode] = useState<Mode>('upload');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(100);
  const [documentId, setDocumentId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [skipProcessed, setSkipProcessed] = useState(true);
  const [presetId, setPresetId] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<XeraPreset | null>(null);
  const [bundleKey, setBundleKey] = useState('');
  const [uploadHasResult, setUploadHasResult] = useState(false);

  // Fetch available bundles
  const { data: bundlesData } = useSWR<BundlesResponse>(
    '/api/registry/bundles?active=true',
    () => api.getBundles(true),
    { refreshInterval: 0 }
  );

  // Default to the default bundle on first load
  useEffect(() => {
    if (bundlesData && !bundleKey) {
      const defaultBundle = bundlesData.bundles.find(b => b.isDefault);
      if (defaultBundle) setBundleKey(defaultBundle.key);
    }
  }, [bundlesData]);
  const [showProdConfirm, setShowProdConfirm] = useState(false);
  const [processAll, setProcessAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [batchResult, setBatchResult] = useState<FeederPageResponse | null>(null);
  const [allResult, setAllResult] = useState<FeederAllResponse | null>(null);
  const [singleResult, setSingleResult] = useState<FeederRecordResponse | null>(null);
  const [batchRecordsResult, setBatchRecordsResult] = useState<FeederBatchRecordsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Justice.gov state
  const [justiceDataset, setJusticeDataset] = useState(1);
  const [justiceStart, setJusticeStart] = useState(1);
  const [justiceEnd, setJusticeEnd] = useState(500);
  const [externalResult, setExternalResult] = useState<ExternalBatchResult | null>(null);
  // Document count check state
  const [documentIdPrefix, setDocumentIdPrefix] = useState('');
  // Document count check state
  const [isCheckingCount, setIsCheckingCount] = useState(false);
  const [documentCountResult, setDocumentCountResult] = useState<DocumentCountResponse | null>(null);
  const [documentCountError, setDocumentCountError] = useState<string | null>(null);

  // Parse comma/newline/space-separated document IDs, deduplicated
  const parsedIds = documentId
    .split(/[,\n\s]+/)
    .map(s => s.trim())
    .filter(Boolean);
  const uniqueIds = [...new Set(parsedIds)];
  const isMultiDoc = uniqueIds.length > 1;

  const handlePresetChange = (id: string, preset: XeraPreset | null) => {
    setPresetId(id);
    setSelectedPreset(preset);
    setShowProdConfirm(false);
    // Reset document count when preset changes
    setDocumentCountResult(null);
    setDocumentCountError(null);
  };

  const handleSizeChange = (newSize: number) => {
    setSize(newSize);
    // Reset document count when page size changes (affects paging detection)
    setDocumentCountResult(null);
    setDocumentCountError(null);
  };

  const handleCheckDocumentCount = async () => {
    if (!presetId) {
      setDocumentCountError('Please select a data source first');
      return;
    }

    setIsCheckingCount(true);
    setDocumentCountError(null);
    setDocumentCountResult(null);

    try {
      // Pass the current page size to match what starting a batch would do
      const result = await api.getDocumentCount(presetId, size);
      setDocumentCountResult(result);
    } catch (err) {
      setDocumentCountError(err instanceof Error ? err.message : 'Failed to check document count');
    } finally {
      setIsCheckingCount(false);
    }
  };

  const handleJusticeSubmit = async () => {
    const count = justiceEnd - justiceStart + 1;
    if (count < 1) {
      setError('End EFTA ID must be >= Start EFTA ID');
      return;
    }
    if (count > 1000) {
      setError('Maximum 1000 documents per submission');
      return;
    }

    setIsLoading(true);
    setError(null);
    setExternalResult(null);

    try {
      const documents: { url: string; documentId: string }[] = [];
      for (let i = justiceStart; i <= justiceEnd; i++) {
        const padded = String(i).padStart(8, '0');
        documents.push({
          url: `https://www.justice.gov/epstein/files/DataSet%20${justiceDataset}/EFTA${padded}.pdf`,
          documentId: `EFTA${padded}`,
        });
      }

      const defaultName = `Epstein Dataset ${justiceDataset} - EFTA${String(justiceStart).padStart(8, '0')}-${String(justiceEnd).padStart(8, '0')}`;
      const response = await api.submitExternalBatch({
        documents,
        batchName: batchName || defaultName,
        folderName: 'epstein-docs',
        skipProcessed,
      });
      setExternalResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit batch');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (confirmProduction = false) => {
    if (mode === 'justice') {
      return handleJusticeSubmit();
    }

    // If production preset and not confirmed, show confirmation dialog
    if (selectedPreset?.environment === 'production' && !confirmProduction) {
      setShowProdConfirm(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setBatchResult(null);
    setAllResult(null);
    setSingleResult(null);
    setBatchRecordsResult(null);
    setShowProdConfirm(false);

    try {
      if (mode === 'batch') {
        if (processAll) {
          // Process all documents (auto-paging)
          const response = await api.processAllDocuments({
            presetId,
            confirmProduction: selectedPreset?.environment === 'production',
            batchName: batchName || undefined,
            skipProcessed,
            pageSize: size,
            documentIdPrefix: documentIdPrefix || undefined,
            bundleKey: bundleKey || undefined,
          });
          setAllResult(response);
        } else {
          // Process single page
          const response = await api.startBatch({
            page,
            size,
            presetId: presetId || undefined,
            confirmProduction: selectedPreset?.environment === 'production',
            batchName: batchName || undefined,
            skipProcessed,
            documentIdPrefix: documentIdPrefix || undefined,
            bundleKey: bundleKey || undefined,
          });
          setBatchResult(response);
        }
      } else {
        // Documents mode (single or multi)
        if (uniqueIds.length === 0) {
          setError('At least one Document ID is required');
          return;
        }

        if (isMultiDoc) {
          // Multiple documents - use batch endpoint
          const response = await api.queueBatchRecords({
            documentIds: uniqueIds,
            presetId,
            confirmProduction: selectedPreset?.environment === 'production',
            skipProcessed,
            batchName: batchName || undefined,
            documentIdPrefix: documentIdPrefix || undefined,
            bundleKey: bundleKey || undefined,
          });
          setBatchRecordsResult(response);
        } else {
          // Single document - use existing endpoint
          const response = await api.queueSingleRecord({
            documentId: uniqueIds[0],
            presetId,
            confirmProduction: selectedPreset?.environment === 'production',
            skipProcessed,
            batchName: batchName || undefined,
            documentIdPrefix: documentIdPrefix || undefined,
            bundleKey: bundleKey || undefined,
          });
          setSingleResult(response);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode === 'batch' ? 'start batch' : 'queue documents'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setBatchResult(null);
    setAllResult(null);
    setSingleResult(null);
    setBatchRecordsResult(null);
    setExternalResult(null);
    setError(null);
    setShowProdConfirm(false);
    setDocumentCountResult(null);
    setDocumentCountError(null);
    setUploadHasResult(false);
    onClose();
  };

  const hasResult = batchResult !== null || allResult !== null || singleResult !== null || batchRecordsResult !== null || externalResult !== null;

  return (
    <Dialog open={isOpen} onClose={handleClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-lg">
        <Title className="text-gray-100">{uploadOnly ? 'Upload Documents' : 'Start New Batch'}</Title>

        {showProdConfirm ? (
          <>
            <div className="mt-4 space-y-4">
              <div className="bg-amber-950/30 border border-amber-900/50 rounded p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-amber-400 text-lg">&#9888;</span>
                  <Text className="text-amber-400 font-medium">Production Environment</Text>
                </div>
                <Text className="text-gray-300 text-sm mb-3">
                  You are about to process documents from:
                </Text>
                <div className="bg-gray-900/50 rounded p-3 mb-3">
                  <Text className="text-white font-medium">{selectedPreset?.name}</Text>
                  <Text className="text-gray-400 text-sm">
                    Project ID: {selectedPreset?.projectId}
                  </Text>
                </div>
                <Text className="text-amber-300 text-sm">
                  This will affect LIVE production data. Are you sure?
                </Text>
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6 gap-3">
              <Button variant="secondary" onClick={() => setShowProdConfirm(false)}>
                Cancel
              </Button>
              <Button color="amber" onClick={() => handleSubmit(true)} loading={isLoading}>
                I understand, proceed
              </Button>
            </Flex>
          </>
        ) : !hasResult ? (
          <>
            <div className="mt-4 space-y-4">
              {/* Mode selector */}
              {!uploadOnly && (
                <div className="flex gap-2 p-1 bg-gray-800 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setMode('upload')}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      mode === 'upload'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    }`}
                  >
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('batch')}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      mode === 'batch'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    }`}
                  >
                    Batch (Page)
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('single')}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      mode === 'single'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    }`}
                  >
                    Documents
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('justice')}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      mode === 'justice'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    }`}
                  >
                    Justice.gov
                  </button>
                </div>
              )}

              {mode === 'upload' ? (
                <>
                  {/* Bundle selector for uploads — hidden once submitted to give progress its own focus */}
                  {!uploadHasResult && (
                    <div className="mb-4">
                      <Text className="text-gray-400 text-sm mb-1">Classifier Bundle</Text>
                      <Select
                        value={bundleKey}
                        onValueChange={setBundleKey}
                        placeholder="Select bundle..."
                      >
                        {(bundlesData?.bundles || []).map(b => (
                          <SelectItem key={b.key} value={b.key}>
                            {b.name}{b.isDefault ? ' (default)' : ''}
                          </SelectItem>
                        ))}
                      </Select>
                      <p className="text-xs text-gray-600 mt-1">
                        Determines which classifiers run and which labels are surfaced.
                      </p>
                    </div>
                  )}
                  <FileUploadTab
                    onClose={handleClose}
                    bundleKey={bundleKey}
                    onResultChange={setUploadHasResult}
                  />
                </>
              ) : mode === 'justice' ? (
                <>
                  <div>
                    <Text className="text-gray-400 text-sm mb-1">Dataset (1-12)</Text>
                    <NumberInput
                      value={justiceDataset}
                      onValueChange={(v) => {
                        setJusticeDataset(v);
                        const range = getKnownRange(v);
                        if (range) {
                          setJusticeStart(range.start);
                          setJusticeEnd(Math.min(range.start + 499, range.end));
                        }
                      }}
                      min={1}
                      max={12}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Text className="text-gray-400 text-sm mb-1">Start EFTA ID</Text>
                      <NumberInput
                        value={justiceStart}
                        onValueChange={setJusticeStart}
                        min={1}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                    <div>
                      <Text className="text-gray-400 text-sm mb-1">End EFTA ID</Text>
                      <NumberInput
                        value={justiceEnd}
                        onValueChange={setJusticeEnd}
                        min={1}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                  </div>
                  {justiceEnd >= justiceStart && (
                    <div className="text-sm text-gray-400">
                      {(justiceEnd - justiceStart + 1).toLocaleString()} documents
                      {justiceEnd - justiceStart + 1 > 1000 && (
                        <span className="text-red-400 ml-1">(max 1000)</span>
                      )}
                      <span className="text-gray-600 ml-2">
                        EFTA{String(justiceStart).padStart(8, '0')}&ndash;EFTA{String(justiceEnd).padStart(8, '0')}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const range = getKnownRange(justiceDataset);
                    return range ? (
                      <div className="text-xs text-gray-600">
                        Dataset {justiceDataset} known range: EFTA {range.start.toLocaleString()}&ndash;{range.end.toLocaleString()}
                      </div>
                    ) : null;
                  })()}
                </>
              ) : (
                <>
                  <div>
                    <Text className="text-gray-400 text-sm mb-1">Data Source</Text>
                    <PresetSelector
                      value={presetId}
                      onChange={handlePresetChange}
                      disabled={isLoading}
                    />
                  </div>

                  {/* Classifier Bundle */}
                  <div>
                    <Text className="text-gray-400 text-sm mb-1">Classifier Bundle</Text>
                    <Select
                      value={bundleKey}
                      onValueChange={setBundleKey}
                      placeholder="Select bundle..."
                      disabled={isLoading}
                    >
                      {(bundlesData?.bundles || []).map(b => (
                        <SelectItem key={b.key} value={b.key}>
                          {b.name}{b.isDefault ? ' (default)' : ''}
                        </SelectItem>
                      ))}
                    </Select>
                    <p className="text-xs text-gray-600 mt-1">
                      Determines which classifiers run and which labels are surfaced.
                    </p>
                  </div>

                  <div>
                    {/* Check Document Count section */}
                    <div className="mt-2">
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={handleCheckDocumentCount}
                        loading={isCheckingCount}
                        disabled={!presetId || isLoading}
                      >
                        Check Document Count
                      </Button>

                      {documentCountError && (
                        <div className="mt-2 text-sm text-red-400">
                          {documentCountError}
                        </div>
                      )}

                      {documentCountResult && (
                        <div className="mt-2 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Text className="text-gray-300 font-medium">
                              {documentCountResult.documentCount.total.toLocaleString()} documents
                            </Text>
                            <Badge
                              color={documentCountResult.documentCount.apiPagingWorks ? 'emerald' : 'amber'}
                              size="xs"
                            >
                              {documentCountResult.documentCount.apiPagingWorks ? 'Paging OK' : 'Paging Issue'}
                            </Badge>
                            {documentCountResult.documentCount.countMethod === 'pagination' && (
                              <Badge color="blue" size="xs">
                                Counted via paging
                              </Badge>
                            )}
                          </div>

                          <div className="text-xs text-gray-400 space-y-1">
                            <div>
                              Total pages: {documentCountResult.documentCount.totalPages.toLocaleString()}
                              {' '}(at {documentCountResult.documentCount.diagnostics.requestedPageSize}/page)
                            </div>
                            {!documentCountResult.documentCount.apiPagingWorks && (
                              <div className="text-amber-400 mt-1">
                                Warning: API returned all {documentCountResult.documentCount.diagnostics.recordsReturned} records
                                when only {documentCountResult.documentCount.diagnostics.requestedPageSize} were requested.
                                Client-side paging will be used.
                              </div>
                            )}
                            {documentCountResult.documentCount.apiPagingWorks &&
                             documentCountResult.documentCount.folderRecordCount !== documentCountResult.documentCount.total && (
                              <div className="text-gray-500 text-[10px] mt-1">
                                API reports {documentCountResult.documentCount.folderRecordCount.toLocaleString()} in folder
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {mode === 'batch' && (
                <>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-700">
                    <input
                      type="checkbox"
                      id="processAll"
                      checked={processAll}
                      onChange={(e) => setProcessAll(e.target.checked)}
                      className="rounded bg-gray-800 border-gray-700"
                    />
                    <label htmlFor="processAll" className="text-gray-400 text-sm">
                      Process all documents (auto-paging)
                    </label>
                    <div className="relative group">
                      <span className="text-gray-500 hover:text-gray-300 cursor-help text-sm">
                        &#9432;
                      </span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg text-xs text-gray-300 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <p className="font-medium text-gray-200 mb-1">When checked:</p>
                        <p className="mb-2">Automatically pages through all documents in the data source and queues them for processing.</p>
                        <p className="font-medium text-gray-200 mb-1">When unchecked:</p>
                        <p>Only processes a single page of documents.</p>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-gray-700"></div>
                      </div>
                    </div>
                  </div>

                  {!processAll && (
                    <div>
                      <Text className="text-gray-400 text-sm mb-1">Page Number</Text>
                      <NumberInput
                        value={page}
                        onValueChange={setPage}
                        min={1}
                        className="bg-gray-800 border-gray-700"
                      />
                    </div>
                  )}

                  <div>
                    <Text className="text-gray-400 text-sm mb-1">
                      {processAll ? 'Page Size for API calls (max 5000)' : 'Page Size (max 5000)'}
                    </Text>
                    <NumberInput
                      value={size}
                      onValueChange={handleSizeChange}
                      min={1}
                      max={5000}
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </>
              )}

              {mode === 'single' && (
                <div>
                  <Text className="text-gray-400 text-sm mb-1">Document ID(s)</Text>
                  <textarea
                    value={documentId}
                    onChange={(e) => setDocumentId(e.target.value)}
                    placeholder="Enter iConect record IDs (e.g., 47916) — comma, space, or newline separated"
                    rows={3}
                    className="w-full rounded-md bg-gray-800 border border-gray-700 text-gray-200 text-sm px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                  />
                  {uniqueIds.length > 0 && (
                    <Text className="text-gray-500 text-xs mt-1">
                      {uniqueIds.length.toLocaleString()} {uniqueIds.length === 1 ? 'document' : 'unique documents'}
                      {parsedIds.length !== uniqueIds.length && (
                        <span className="text-amber-400"> ({parsedIds.length - uniqueIds.length} duplicates removed)</span>
                      )}
                    </Text>
                  )}
                </div>
              )}

              {mode !== 'upload' && (
              <div>
                <Text className="text-gray-400 text-sm mb-1">
                  {mode === 'single' && !isMultiDoc ? 'Label (optional)' : 'Batch Name (optional)'}
                </Text>
                <TextInput
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder={
                    mode === 'batch'
                      ? (selectedPreset ? `${selectedPreset.name} - Page ${page} (${size}/page)` : 'Auto-generated from data source')
                      : mode === 'justice'
                        ? `Epstein Dataset ${justiceDataset} - EFTA${String(justiceStart).padStart(8, '0')}-${String(justiceEnd).padStart(8, '0')}`
                        : isMultiDoc
                          ? `${uniqueIds.length.toLocaleString()} records by ID`
                          : `Single record: ${uniqueIds[0] || '...'}`
                  }
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              )}

              {mode !== 'justice' && mode !== 'upload' && (
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Text className="text-gray-400 text-sm">
                      Document ID Prefix (optional)
                    </Text>
                    <div className="relative group">
                      <span className="text-gray-500 hover:text-gray-300 cursor-help text-sm">
                        &#9432;
                      </span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg text-xs text-gray-300 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <p className="font-medium text-gray-200 mb-1">Re-run &amp; compare batches</p>
                        <p className="mb-2">Prepends a prefix to each document ID (e.g. record <span className="font-mono">47916</span> becomes <span className="font-mono">v2-47916</span>), creating new unique IDs so the same source documents can be processed again without being skipped by idempotency.</p>
                        <p className="font-medium text-gray-200 mb-1">Comparing runs</p>
                        <p>Use the compare-extractions script with <span className="font-mono">--left-prefix</span> and <span className="font-mono">--right-prefix</span> to diff extraction results between two prefixed runs.</p>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-gray-700"></div>
                      </div>
                    </div>
                  </div>
                  <TextInput
                    value={documentIdPrefix}
                    onChange={(e) => setDocumentIdPrefix(e.target.value)}
                    placeholder="e.g. v2, ocr-test, run2"
                    className="bg-gray-800 border-gray-700"
                  />
                  {documentIdPrefix && (
                    <Text className="text-gray-500 text-xs mt-1">
                      IDs will be prefixed: <span className="text-gray-300 font-mono">{documentIdPrefix}-12345</span>
                    </Text>
                  )}
                </div>
              )}

              {mode !== 'upload' && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="skipProcessed"
                  checked={skipProcessed}
                  onChange={(e) => setSkipProcessed(e.target.checked)}
                  className="rounded bg-gray-800 border-gray-700"
                />
                <label htmlFor="skipProcessed" className="text-gray-400 text-sm">
                  Skip already processed documents
                </label>
                <div className="relative group">
                  <span className="text-gray-500 hover:text-gray-300 cursor-help text-sm">
                    &#9432;
                  </span>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg shadow-lg text-xs text-gray-300 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <p className="font-medium text-gray-200 mb-1">When checked:</p>
                    <p className="mb-2">Documents already processed or in progress will be skipped.</p>
                    <p className="font-medium text-gray-200 mb-1">When unchecked:</p>
                    <p>All documents will be reprocessed from scratch, even if previously completed.</p>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-gray-700"></div>
                  </div>
                </div>
              </div>
              )}

              {mode !== 'upload' && error && (
                <div className="bg-red-950/30 border border-red-900/50 rounded p-2">
                  <Text className="text-red-400 text-sm">{error}</Text>
                </div>
              )}
            </div>

            {mode !== 'upload' && (
            <Flex justifyContent="end" className="mt-6 gap-3">
              <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit()} loading={isLoading}>
                {mode === 'batch' ? 'Start Batch' : mode === 'justice' ? 'Submit URLs' : isMultiDoc ? `Queue ${uniqueIds.length.toLocaleString()} Documents` : 'Queue Document'}
              </Button>
            </Flex>
            )}
          </>
        ) : externalResult ? (
          // External batch (Justice.gov) result
          <>
            <div className="mt-4 space-y-3">
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded p-3">
                <Text className="text-emerald-400 font-medium">
                  {externalResult.summary.total > 0 ? 'URLs Submitted' : 'No Documents Submitted'}
                </Text>
              </div>

              <div className="space-y-2 text-sm">
                {externalResult.batchId && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Batch ID</Text>
                    <Text className="text-gray-300 font-mono">{externalResult.batchId}</Text>
                  </Flex>
                )}
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Batch Name</Text>
                  <Text className="text-gray-300">{externalResult.batchName}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Total</Text>
                  <Text className="text-gray-300">{externalResult.summary.total.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Queued</Text>
                  <Text className="text-emerald-400">{externalResult.summary.queued.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Skipped</Text>
                  <Text className="text-gray-400">{externalResult.summary.skipped.toLocaleString()}</Text>
                </Flex>
                {externalResult.summary.failed > 0 && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Failed</Text>
                    <Text className="text-red-400">{externalResult.summary.failed.toLocaleString()}</Text>
                  </Flex>
                )}
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </Flex>
          </>
        ) : singleResult ? (
          // Single document result
          <>
            <div className="mt-4 space-y-3">
              <div className={`rounded p-3 ${
                singleResult.skipped
                  ? 'bg-amber-950/30 border border-amber-900/50'
                  : 'bg-emerald-950/30 border border-emerald-900/50'
              }`}>
                <Text className={singleResult.skipped ? 'text-amber-400 font-medium' : 'text-emerald-400 font-medium'}>
                  {singleResult.skipped ? 'Document Already Processed' : 'Document Queued Successfully'}
                </Text>
              </div>

              <div className="space-y-2 text-sm">
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Document ID</Text>
                  <Text className="text-gray-300 font-mono">{singleResult.documentId}</Text>
                </Flex>
                {singleResult.flowId && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Flow ID</Text>
                    <Text className="text-gray-300 font-mono">{singleResult.flowId}</Text>
                  </Flex>
                )}
                {singleResult.preset && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Data Source</Text>
                    <Flex className="gap-2">
                      <Text className="text-gray-300">{singleResult.preset.name}</Text>
                      {singleResult.preset.environment === 'production' && (
                        <Badge color="red" size="xs">PROD</Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                {singleResult.skipped && (
                  <div className="mt-2 text-gray-400 text-xs">
                    To reprocess, uncheck "Skip already processed documents"
                  </div>
                )}
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </Flex>
          </>
        ) : batchRecordsResult ? (
          // Batch records by ID result
          <>
            <div className="mt-4 space-y-3">
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded p-3">
                <Text className="text-emerald-400 font-medium">
                  {batchRecordsResult.summary.queued > 0 ? 'Documents Queued Successfully' : 'No Documents Queued'}
                </Text>
              </div>

              <div className="space-y-2 text-sm">
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Batch ID</Text>
                  <Text className="text-gray-300 font-mono">{batchRecordsResult.batchId}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Batch Name</Text>
                  <Text className="text-gray-300">{batchRecordsResult.batchName}</Text>
                </Flex>
                {batchRecordsResult.preset && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Data Source</Text>
                    <Flex className="gap-2">
                      <Text className="text-gray-300">{batchRecordsResult.preset.name}</Text>
                      {batchRecordsResult.preset.environment === 'production' && (
                        <Badge color="red" size="xs">PROD</Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Total</Text>
                  <Text className="text-gray-300">{batchRecordsResult.summary.total.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Queued</Text>
                  <Text className="text-emerald-400">{batchRecordsResult.summary.queued.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Skipped</Text>
                  <Text className="text-gray-400">{batchRecordsResult.summary.skipped.toLocaleString()}</Text>
                </Flex>
                {batchRecordsResult.summary.failed > 0 && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Failed</Text>
                    <Text className="text-red-400">{batchRecordsResult.summary.failed.toLocaleString()}</Text>
                  </Flex>
                )}
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </Flex>
          </>
        ) : allResult ? (
          // Process All result
          <>
            <div className="mt-4 space-y-3">
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded p-3">
                <Text className="text-emerald-400 font-medium">All Documents Queued Successfully</Text>
              </div>

              <div className="space-y-2 text-sm">
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Batch ID</Text>
                  <Text className="text-gray-300 font-mono">{allResult.batchId}</Text>
                </Flex>
                {allResult.preset && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Data Source</Text>
                    <Flex className="gap-2">
                      <Text className="text-gray-300">{allResult.preset.name}</Text>
                      {allResult.preset.environment === 'production' && (
                        <Badge color="red" size="xs">PROD</Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Total Pages Fetched</Text>
                  <Text className="text-gray-300">{allResult.summary.totalPages.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Total Records Found</Text>
                  <Text className="text-gray-300">{allResult.summary.totalRecords.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Queued</Text>
                  <Text className="text-emerald-400">{allResult.summary.queued.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Skipped</Text>
                  <Text className="text-gray-400">{allResult.summary.skipped.toLocaleString()}</Text>
                </Flex>
                {allResult.summary.failed > 0 && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Failed</Text>
                    <Text className="text-red-400">{allResult.summary.failed.toLocaleString()}</Text>
                  </Flex>
                )}
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </Flex>
          </>
        ) : batchResult ? (
          // Batch result
          <>
            <div className="mt-4 space-y-3">
              <div className="bg-emerald-950/30 border border-emerald-900/50 rounded p-3">
                <Text className="text-emerald-400 font-medium">Batch Started Successfully</Text>
              </div>

              <div className="space-y-2 text-sm">
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Batch ID</Text>
                  <Text className="text-gray-300 font-mono">{batchResult.batchId}</Text>
                </Flex>
                {batchResult.preset && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Data Source</Text>
                    <Flex className="gap-2">
                      <Text className="text-gray-300">{batchResult.preset.name}</Text>
                      {batchResult.preset.environment === 'production' && (
                        <Badge color="red" size="xs">PROD</Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Records in Folder</Text>
                  <Text className="text-gray-300">{batchResult.folderRecordCount.toLocaleString()}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Page</Text>
                  <Text className="text-gray-300">
                    {batchResult.pagination.page} of {batchResult.pagination.totalPages}
                  </Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Records Found</Text>
                  <Text className="text-gray-300">{batchResult.summary.recordsFound}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Queued</Text>
                  <Text className="text-emerald-400">{batchResult.summary.queued}</Text>
                </Flex>
                <Flex justifyContent="between">
                  <Text className="text-gray-500">Skipped</Text>
                  <Text className="text-gray-400">{batchResult.summary.skipped}</Text>
                </Flex>
                {batchResult.summary.failed > 0 && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Failed</Text>
                    <Text className="text-red-400">{batchResult.summary.failed}</Text>
                  </Flex>
                )}
                {batchResult.nextPage && (
                  <Flex justifyContent="between">
                    <Text className="text-gray-500">Next Page</Text>
                    <Text className="text-blue-400">{batchResult.nextPage}</Text>
                  </Flex>
                )}
              </div>
            </div>

            <Flex justifyContent="end" className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </Flex>
          </>
        ) : null}
      </DialogPanel>
    </Dialog>
  );
}
